/**
 * A TypeScript port of `autofocus-core`'s fold engine.
 *
 * This is a *port*, not a reimplementation: it exists because the browser has to
 * run the same engine as the server (ARCHITECTURE.md §4.1), and the two
 * implementations are held together by the cross-node tests in
 * `crates/autofocus-core/tests/`. Any divergence here is a replication bug, not
 * a style choice — which is why the structural rules are repeated rather than
 * assumed:
 *
 * * `page_id` is a pure function of `(notebook_id, page_number)` (UUIDv5 over a
 *   fixed name), so a client can seal a page and roll over offline with nothing
 *   to reconcile (§3.1).
 * * `appendTask` VALIDATES the server's `Assigned` against a locally computed
 *   plan instead of trusting it. Without that, a client would happily render a
 *   colliding slot and diverge from the server.
 * * `fold` sorts by `seq`; `apply` is atomic per op.
 */

import type {
  Assigned,
  CommittedEnvelope,
  Notebook,
  Op,
  Page,
  PageStatus,
  Projection,
  RenderModel,
  SyncEnvelope,
  Task,
  TaskState,
} from '../types/generated';

// ── Deterministic page ids (§3.1, §13.1) ─────────────────────────────────────

const NAMESPACE_DNS = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';

/** The 16 namespace bytes, in wire order — the prefix RFC 4122 §4.3 requires. */
function namespaceBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * SHA-1 over the concatenation of `parts`.
 *
 * The parts are separate because UUIDv5 hashes `namespace || name` — hashing
 * the name alone still yields 16 bytes and still *looks* like a UUID, which is
 * how this port came to disagree with `engine::derive_page_id` while every
 * self-referential test stayed green. See `tests/page_id_vectors.test.ts`.
 */
async function sha1(...parts: Uint8Array[]): Promise<Uint8Array> {
  const joined = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const part of parts) {
    joined.set(part, at);
    at += part.length;
  }
  return new Uint8Array(await crypto.subtle.digest('SHA-1', joined));
}

function toUuidString(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * UUIDv5 (SHA-1, DNS namespace) over `"autofocus:notebook:{id}:page:{n}"`.
 *
 * Async because WebCrypto's digest is. Every page id in a projection is derived
 * once at fold time and then read off the projection, so the awaits happen
 * during the fold rather than during render.
 */
export async function derivePageId(
  notebookId: string,
  pageNumber: number,
): Promise<string> {
  const name = `autofocus:notebook:${notebookId}:page:${pageNumber}`;
  const digest = await sha1(namespaceBytes(NAMESPACE_DNS), new TextEncoder().encode(name));
  const bytes = new Uint8Array(16);
  bytes.set(digest.subarray(0, 16), 0);

  // Version 5 and the RFC 4122 variant.
  bytes[6] = 0x50 | (bytes[6]! & 0x0f);
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);
  return toUuidString(bytes);
}

/**
 * All page ids a notebook can need, derived in one pass.
 *
 * Pages are opened one at a time by a seal, so deriving lazily would mean an
 * `await` inside the middle of a fold. Precomputing a window keeps `apply`
 * synchronous, which is what makes the fold a single readable function.
 */
export async function derivePageIds(
  notebookId: string,
  count: number,
): Promise<Map<number, string>> {
  const out = new Map<number, string>();
  for (let n = 1; n <= count; n += 1) {
    out.set(n, await derivePageId(notebookId, n));
  }
  return out;
}

// ── The fold context ──────────────────────────────────────────────────────────

/**
 * Everything `apply` needs beyond the op itself.
 *
 * `pageIds` is passed in rather than derived inside, because deriving is async
 * and the fold must stay synchronous (§I2: pure, no clock, no RNG).
 */
export interface FoldContext {
  pageIds: Map<number, string>;
}

export class MalformedEnvelope extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedEnvelope';
  }
}

// ── Projection construction ───────────────────────────────────────────────────

/**
 * Tag format is engine validation (A19): the tag is the notebook's
 * `@`-address, so any whitespace character makes it unroutable. The EMPTY tag
 * passes — it is the pre-A19 serde default, and rejecting it would make every
 * existing notebook's history fail to replay. Mirror of `fold.rs`'s
 * `validate_tag_format`.
 *
 * The two engines must agree on what counts as whitespace: JS `/\s/` includes
 * U+FEFF (which Rust's `char::is_whitespace` excludes) but not U+0085 (which
 * Rust includes) — the union is what keeps a tag the server accepts from being
 * one the browser refuses, which would be a committed-log divergence.
 */
function validateTagFormat(tag: string): void {
  if (/\s|\u{0085}/u.test(tag)) {
    throw new MalformedEnvelope('tag must not contain whitespace');
  }
}

/** The nil shell a fold starts from (A16): page 1 is opened by CreateNotebook. */
export function emptyProjection(): Projection {
  const nil = '00000000-0000-0000-0000-000000000000';
  return {
    notebook_id: nil,
    notebook: {
      id: nil,
      owner_id: nil,
      name: '',
      tag: '',
      description: '',
      page_capacity: 25,
      timezone: 'UTC',
    },
    head_seq: 0,
    tasks: {},
    pages: {},
  };
}

/** The writing page, if one is open. */
function writingPage(projection: Projection): Page | undefined {
  return Object.values(projection.pages).find((p) => p.status === 'Writing');
}

/** The count of tasks on a page — which is the next free `line_index`. */
function taskCountOnPage(projection: Projection, pageId: string): number {
  return Object.values(projection.tasks).filter((t) => t.page_id === pageId).length;
}

/**
 * Plans the allocation for one `AppendTask` — the mirror of
 * `engine::compute_append_allocation`.
 *
 * Returns `null` when the notebook has no open page, which `apply` turns into
 * a `MalformedEnvelope` (an op sequenced before `CreateNotebook`).
 */
export function planAppend(
  projection: Projection,
  taskId: string,
  logicalDay: string,
  logicalDaySource: Assigned['logical_day_source'],
): Assigned | null {
  const capacity = projection.notebook.page_capacity;
  const writing = writingPage(projection);
  if (!writing) return null;

  const lineIndex = taskCountOnPage(projection, writing.id);
  if (lineIndex >= capacity) return null;

  const seals = lineIndex === capacity - 1;
  return {
    task_id: taskId,
    page_id: writing.id,
    page_number: writing.page_number,
    line_index: lineIndex,
    sealed_page_id: seals ? writing.id : null,
    logical_day: logicalDay,
    logical_day_source: logicalDaySource,
  };
}

/**
 * Plans the allocations for a `ReenterTask`'s children — the mirror of
 * `engine::compute_child_allocations`, including a run that crosses a seal.
 */
export function planChildren(
  projection: Projection,
  ctx: FoldContext,
  children: Array<{ task_id: string; title: string }>,
  logicalDay: string,
  logicalDaySource: Assigned['logical_day_source'],
): Assigned[] {
  const capacity = projection.notebook.page_capacity;
  const writing = writingPage(projection);
  if (!writing) return [];

  let pageId = writing.id;
  let pageNumber = writing.page_number;
  let nextSlot = taskCountOnPage(projection, pageId);
  const plan: Assigned[] = [];

  for (const child of children) {
    if (nextSlot >= capacity) break;
    const seals = nextSlot === capacity - 1;
    plan.push({
      task_id: child.task_id,
      page_id: pageId,
      page_number: pageNumber,
      line_index: nextSlot,
      sealed_page_id: seals ? pageId : null,
      logical_day: logicalDay,
      logical_day_source: logicalDaySource,
    });
    if (seals) {
      pageNumber += 1;
      // The page this seal will OPEN does not exist yet, so its id comes from
      // the precomputed window rather than from the projection. Returning a
      // short plan makes `apply` reject the envelope — which is the correct
      // outcome, because a plan that needs a page id we cannot derive is not a
      // plan we can validate.
      const nextId = ctx.pageIds.get(pageNumber);
      if (!nextId) return plan;
      pageId = nextId;
      nextSlot = 0;
    } else {
      nextSlot += 1;
    }
  }
  return plan;
}

/**
 * Seals the writing page and opens the next one, using the precomputed id.
 *
 * This is `engine::allocate_slot_at`: a seal never renumbers anything, it only
 * marks the page and creates the next (guarantee 1).
 */
function allocateSlot(
  projection: Projection,
  ctx: FoldContext,
  pageNumber: number,
  lineIndex: number,
  seq: number,
  serverTime: number,
): { pageId: string; pageNumber: number } {
  const writing = writingPage(projection)!;
  const capacity = projection.notebook.page_capacity;

  if (lineIndex === capacity - 1) {
    projection.pages[writing.id] = {
      ...writing,
      status: 'Active',
      sealed_at_seq: seq,
    };

    const nextNumber = pageNumber + 1;
    const nextId = ctx.pageIds.get(nextNumber);
    if (nextId) {
      projection.pages[nextId] = {
        id: nextId,
        notebook_id: projection.notebook_id,
        page_number: nextNumber,
        status: 'Writing',
        sealed_at_seq: null,
        created_at: serverTime,
      };
    }
  }

  return { pageId: writing.id, pageNumber };
}

/**
 * Re-derives a page's terminal status from its tasks (A12).
 *
 * Cached on the page row but re-derived here — the fold is the definition, and a
 * cached value is only ever a checkpoint of it.
 */
function updatePageStatus(projection: Projection, pageId: string): void {
  const page = projection.pages[pageId];
  if (!page || page.status === 'Writing') return;

  let anyActive = false;
  let anyDismissed = false;
  for (const task of Object.values(projection.tasks)) {
    if (task.page_id !== pageId) continue;
    if (task.state === 'Active') anyActive = true;
    if (task.state === 'Dismissed') anyDismissed = true;
  }

  const status: PageStatus = anyActive
    ? 'Active'
    : anyDismissed
      ? 'DeadDismissed'
      : 'DeadCompleted';
  projection.pages[pageId] = { ...page, status };
}

/** `assigned` equality, field by field — every server-owned field matters. */
function sameAssigned(a: Assigned, b: Assigned): boolean {
  return (
    a.task_id === b.task_id &&
    a.page_id === b.page_id &&
    a.page_number === b.page_number &&
    a.line_index === b.line_index &&
    (a.sealed_page_id ?? null) === (b.sealed_page_id ?? null) &&
    a.logical_day === b.logical_day &&
    a.logical_day_source === b.logical_day_source
  );
}

/**
 * The single state machine: one operation, applied atomically.
 *
 * Every precondition is evaluated before any mutation, so a rejected op leaves
 * the projection byte-identical (REPLICATION.md §5.6). Throwing `MalformedEnvelope`
 * is the rejection signal — there is no partial application to clean up.
 */
export function apply(
  projection: Projection,
  ctx: FoldContext,
  envelope: CommittedEnvelope,
): Assigned | null {
  const op: Op = envelope.op;
  const serverTime = envelope.server_time;

  switch (op.type) {
    case 'CreateNotebook': {
      if (op.page_capacity === 0) throw new MalformedEnvelope('page_capacity must be > 0');
      validateTagFormat(op.tag ?? '');
      // Exactly one per notebook, and it must be first: an existing page set
      // marks a second CreateNotebook (§4.4, A16).
      if (Object.keys(projection.pages).length > 0) {
        throw new MalformedEnvelope('CreateNotebook must be the first op');
      }

      const page1 = ctx.pageIds.get(1);
      if (!page1) throw new MalformedEnvelope('page 1 id not derivable');

      projection.notebook = {
        ...projection.notebook,
        id: projection.notebook_id,
        owner_id: envelope.owner_id,
        name: op.name,
        // A19: absent fields are the pre-A19 serde default — replay keeps
        // them empty rather than failing (Review Focus #3).
        tag: op.tag ?? '',
        description: op.description ?? '',
        page_capacity: op.page_capacity,
        timezone: op.timezone,
      };
      projection.pages[page1] = {
        id: page1,
        notebook_id: projection.notebook_id,
        page_number: 1,
        status: 'Writing',
        sealed_at_seq: null,
        created_at: serverTime,
      };
      return null;
    }

    case 'AppendTask': {
      const assigned = envelope.assigned;
      if (!assigned) throw new MalformedEnvelope('AppendTask requires an Assigned');
      if (assigned.task_id !== op.task_id) {
        throw new MalformedEnvelope('Assigned.task_id disagrees with the op body');
      }

      // VALIDATE the server's plan rather than trust it. This is the mirror of
      // the Rust boundary, and the reason a client cannot diverge from the
      // server by rendering a colliding slot.
      const expected = planAppend(
        projection,
        op.task_id,
        assigned.logical_day,
        assigned.logical_day_source,
      );
      if (!expected || !sameAssigned(assigned, expected)) {
        throw new MalformedEnvelope('Assigned disagrees with the local plan');
      }

      if (projection.tasks[op.task_id]) return null; // idempotent

      const { pageId } = allocateSlot(
        projection,
        ctx,
        assigned.page_number,
        assigned.line_index,
        envelope.seq,
        serverTime,
      );

      projection.tasks[op.task_id] = {
        task_id: op.task_id,
        notebook_id: projection.notebook_id,
        page_id: pageId,
        line_index: assigned.line_index,
        title: op.title,
        state: 'Active',
        parent_task_id: op.parent_task_id,
        created_at: serverTime,
        logical_day: assigned.logical_day,
        completed_at: null,
        dismissed_at: null,
      };
      return assigned;
    }

    case 'FinishTask': {
      const task = projection.tasks[op.task_id];
      if (!task) throw new MalformedEnvelope('TASK_NOT_FOUND');
      if (task.state !== 'Active') throw new MalformedEnvelope('TASK_ALREADY_SETTLED');

      projection.tasks[op.task_id] = {
        ...task,
        state: 'Done',
        completed_at: serverTime,
      };
      updatePageStatus(projection, task.page_id);
      return null;
    }

    case 'ReenterTask': {
      if (op.children.length === 0) {
        throw new MalformedEnvelope('ReenterTask requires at least one child');
      }
      const parent = projection.tasks[op.task_id];
      if (!parent) throw new MalformedEnvelope('TASK_NOT_FOUND');
      if (parent.state !== 'Active') {
        throw new MalformedEnvelope('TASK_ALREADY_SETTLED');
      }

      // A15: positions are read from the envelope, never derived. The envelope
      // must match what the pure planner produces over this prefix.
      if (envelope.child_assigned.length !== op.children.length) {
        throw new MalformedEnvelope('child_assigned length mismatch');
      }
      const first = envelope.child_assigned[0]!;
      if (
        !envelope.assigned ||
        !sameAssigned(envelope.assigned, first)
      ) {
        throw new MalformedEnvelope('assigned must mirror child_assigned[0]');
      }
      for (const [i, child] of op.children.entries()) {
        if (envelope.child_assigned[i]!.task_id !== child.task_id) {
          throw new MalformedEnvelope('child_assigned order disagrees with children');
        }
      }
      const expected = planChildren(
        projection,
        ctx,
        op.children,
        first.logical_day,
        first.logical_day_source,
      );
      if (
        expected.length !== envelope.child_assigned.length ||
        !expected.every((a, i) => sameAssigned(a, envelope.child_assigned[i]!))
      ) {
        throw new MalformedEnvelope('child_assigned disagrees with the local plan');
      }

      projection.tasks[op.task_id] = {
        ...parent,
        state: 'Reentered',
        completed_at: serverTime,
      };

      for (const [i, child] of op.children.entries()) {
        const a = envelope.child_assigned[i]!;
        const { pageId } = allocateSlot(
          projection,
          ctx,
          a.page_number,
          a.line_index,
          envelope.seq,
          serverTime,
        );
        projection.tasks[child.task_id] = {
          task_id: child.task_id,
          notebook_id: projection.notebook_id,
          page_id: pageId,
          line_index: a.line_index,
          title: child.title,
          state: 'Active',
          parent_task_id: op.task_id,
          created_at: serverTime,
          logical_day: a.logical_day,
          completed_at: null,
          dismissed_at: null,
        };
      }

      updatePageStatus(projection, parent.page_id);
      return envelope.assigned;
    }

    case 'DismissTask': {
      const task = projection.tasks[op.task_id];
      if (!task) throw new MalformedEnvelope('TASK_NOT_FOUND');
      if (task.state !== 'Active') throw new MalformedEnvelope('TASK_ALREADY_SETTLED');

      projection.tasks[op.task_id] = {
        ...task,
        state: 'Dismissed',
        dismissed_at: serverTime,
      };
      updatePageStatus(projection, task.page_id);
      return null;
    }

    case 'DismissPage': {
      const page = projection.pages[op.page_id];
      if (!page) throw new MalformedEnvelope('PAGE_NOT_FOUND');
      // I8: the writing page is immune; anything not ACTIVE cannot be dismissed.
      if (page.status === 'Writing') throw new MalformedEnvelope('FINAL_PAGE_IMMUNE');
      if (page.status !== 'Active') throw new MalformedEnvelope('PAGE_NOT_ACTIVE');

      for (const task of Object.values(projection.tasks)) {
        if (task.page_id === op.page_id && task.state === 'Active') {
          projection.tasks[task.task_id] = {
            ...task,
            state: 'Dismissed',
            dismissed_at: serverTime,
          };
        }
      }

      updatePageStatus(projection, op.page_id);
      return null;
    }

    case 'RewordTask': {
      const task = projection.tasks[op.task_id];
      if (!task) throw new MalformedEnvelope('TASK_NOT_FOUND');

      projection.tasks[op.task_id] = { ...task, title: op.title };

      // §4.1: RewordTask on a DISMISSED task returns it to ACTIVE. The slot is
      // untouched (I4). (No UI surface authors this since the 2026-10-05
      // amendment — the Graveyard resurrects through an `AppendTask` re-entry
      // instead — but it remains valid engine semantics, tested in both
      // folds.)
      if (task.state === 'Dismissed') {
        projection.tasks[op.task_id] = {
          ...projection.tasks[op.task_id]!,
          state: 'Active',
          dismissed_at: null,
        };
        updatePageStatus(projection, task.page_id);
      }
      return null;
    }

    case 'RestoreTask': {
      const task = projection.tasks[op.task_id];
      if (!task) throw new MalformedEnvelope('TASK_NOT_FOUND');
      if (task.state !== 'Dismissed') {
        throw new MalformedEnvelope('TASK_ALREADY_SETTLED');
      }

      projection.tasks[op.task_id] = {
        ...task,
        state: 'Active',
        dismissed_at: null,
      };
      updatePageStatus(projection, task.page_id);
      return null;
    }

    case 'RenameNotebook': {
      if (Object.keys(projection.pages).length === 0) {
        throw new MalformedEnvelope('notebook was never created');
      }
      // A supplied tag is re-validated (format) here; its UNIQUENESS is the
      // relay's precondition (TAG_TAKEN) — no client projection can see
      // across notebooks. Absent/null means unchanged.
      const tag = op.tag ?? null;
      if (tag !== null) validateTagFormat(tag);
      const description = op.description ?? null;
      projection.notebook = {
        ...projection.notebook,
        name: op.name,
        ...(tag !== null ? { tag } : {}),
        ...(description !== null ? { description } : {}),
      };
      return null;
    }

    default: {
      // Exhaustiveness guard: adding an op to the wire type without handling it
      // here becomes a compile error rather than a silent no-op.
      const never: never = op;
      throw new MalformedEnvelope(`unhandled op: ${JSON.stringify(never)}`);
    }
  }
}

// ── fold: total, over committed ops only ──────────────────────────────────────

/**
 * The definition of committed state (I2, I7).
 *
 * Total: a committed prefix contains no rejecting operations — that is an
 * invariant of the commit path — so a rejected op in the input is a corrupt log
 * and is surfaced rather than skipped.
 *
 * Sorts by `seq`: the function is of the op SET, not of the caller's order.
 */
export function fold(
  envelopes: CommittedEnvelope[],
  ctx: FoldContext,
): Projection {
  const ordered = [...envelopes].sort((a, b) => a.seq - b.seq);
  const projection = emptyProjection();
  if (ordered.length === 0) return projection;

  const notebookId = ordered[0]!.notebook_id;
  projection.notebook_id = notebookId;

  for (const envelope of ordered) {
    // A Projection is scoped to ONE notebook (§4.2.1); a mixed log is a caller
    // bug that must be loud.
    if (envelope.notebook_id !== notebookId) {
      throw new MalformedEnvelope('fold: all envelopes must belong to the same notebook');
    }
    apply(projection, ctx, envelope);
    projection.head_seq = envelope.seq;
  }
  return projection;
}

/**
 * Folds the tail of the log onto a snapshot (REPLICATION.md §11.1).
 *
 * Bootstrap is *snapshot + short tail*, not a whole-log replay — which is the
 * entire point of a resync. `fold` seeds a nil shell and applies from seq 1, so
 * it cannot consume a snapshot directly; this is the entry point that can.
 *
 * The snapshot's `head_seq` is the base's, and `apply` advances it per op, so
 * the result equals `fold(whole log)` whenever the snapshot really was the fold
 * output at that seq — the property `snapshot_preserves_open_visit` asserts on
 * the Rust side.
 */
export function foldFromSnapshot(
  base: Projection,
  tail: CommittedEnvelope[],
  ctx: FoldContext,
): Projection {
  const projection = structuredClone(base);

  for (const envelope of [...tail].sort((a, b) => a.seq - b.seq)) {
    if (envelope.notebook_id !== projection.notebook_id) {
      throw new MalformedEnvelope('fold: all envelopes must belong to the same notebook');
    }
    apply(projection, ctx, envelope);
    projection.head_seq = envelope.seq;
  }
  return projection;
}

// ── fold_pending: the display-grade prediction ────────────────────────────────

/**
 * The logical day with the 4:00 AM rollover in the notebook's own zone (§7.2).
 *
 * IANA zone handling uses `Intl`, deliberately: the *prediction* may read the
 * host clock and zone (§7.2a exempts `fold_pending` from both), whereas `fold`
 * reads a finished `NaiveDate` off the envelope and needs no zone at all.
 */
export function resolveLogicalDay(
  timestampMillis: number,
  hint: string | null,
  timezone: string,
): { day: string; source: 'ClientHint' | 'Server' } {
  const date = new Date(timestampMillis);

  // Local wall-clock parts in the notebook's zone.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const year = Number(parts.find((p) => p.type === 'year')!.value);
  const month = Number(parts.find((p) => p.type === 'month')!.value);
  const day = Number(parts.find((p) => p.type === 'day')!.value);
  let hour = Number(parts.find((p) => p.type === 'hour')!.value);
  if (hour === 24) hour = 0; // Intl reports midnight as 24 in some locales

  // The rollover: before 4 AM the work belongs to the previous day.
  const serverDay = toIsoDate(year, month, day, hour < 4 ? -1 : 0);

  if (hint) {
    const diff = daysBetween(hint, serverDay);
    // ±1 day clamp: honours a realistic offline gap without letting a wrong
    // clock scatter markers across the year (§7.2).
    if (Math.abs(diff) <= 1) return { day: hint, source: 'ClientHint' };
  }
  return { day: serverDay, source: 'Server' };
}

function toIsoDate(year: number, month: number, day: number, dayOffset: number): string {
  const d = new Date(Date.UTC(year, month - 1, day + dayOffset));
  return d.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  const ms = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * Folds un-sequenced local ops over a committed projection.
 *
 * Fallible by design (§5.6 consequence 2): an op the engine rejects is DROPPED
 * from the prediction and never transmitted, because the server evaluates the
 * same preconditions over the same prefix. Its result is display-grade and is
 * never persisted.
 */
export function foldPending(
  committed: Projection,
  pending: SyncEnvelope[],
  ctx: FoldContext,
): Projection {
  const projection = structuredClone(committed);

  for (const envelope of pending) {
    const { day, source } = resolveLogicalDay(
      envelope.client_time,
      envelope.logical_day_hint,
      projection.notebook.timezone,
    );

    const synthetic = toSyntheticCommitted(envelope, projection, ctx, day, source);
    try {
      apply(projection, ctx, synthetic);
    } catch {
      // Dropped: see the doc comment. Not surfaced here — the caller decides
      // what to tell the user.
    }
    projection.head_seq = synthetic.seq;
  }
  return projection;
}

/** Builds the `CommittedEnvelope` shape the engine consumes from a pending one. */
function toSyntheticCommitted(
  envelope: SyncEnvelope,
  projection: Projection,
  ctx: FoldContext,
  logicalDay: string,
  logicalDaySource: 'ClientHint' | 'Server',
): CommittedEnvelope {
  const op = envelope.op;
  let assigned: Assigned | null = null;
  let childAssigned: Assigned[] = [];

  if (op.type === 'AppendTask') {
    assigned = planAppend(projection, op.task_id, logicalDay, logicalDaySource);
  } else if (op.type === 'ReenterTask') {
    childAssigned = planChildren(
      projection,
      ctx,
      op.children,
      logicalDay,
      logicalDaySource,
    );
    assigned = childAssigned[0] ?? null;
  }

  return {
    // A synthetic seq above the committed head. Never persisted, never compared
    // with the server's numbering — it exists only to drive the engine.
    seq: projection.head_seq + 1,
    server_time: envelope.client_time,
    op_id: envelope.op_id,
    owner_id: envelope.owner_id,
    device_id: envelope.device_id,
    notebook_id: envelope.notebook_id,
    op,
    assigned,
    child_assigned: childAssigned,
  };
}

// ── The render model ──────────────────────────────────────────────────────────

/** A task whose committed slot differed from its predicted one (PENDING → ASSIGNED). */
export interface RelayoutEntry {
  task_id: string;
  old_page_id: string;
  old_line_index: number;
  new_page_id: string;
  new_line_index: number;
}

/**
 * Builds `RenderModel` from the two op lists (§6.2).
 *
 * `committed` is the only durable state; `predicted` is a hypothesis over a
 * different prefix and is recomputed from scratch on every outbox change, which
 * is cheap because its length is bounded by the outbox rather than the log.
 */
export function renderModel(
  committedOps_: CommittedEnvelope[],
  pending: SyncEnvelope[],
  ctx: FoldContext,
): RenderModel {
  return {
    committed: fold(committedOps_, ctx),
    predicted: foldPending(fold(committedOps_, ctx), pending, ctx),
  };
}

/**
 * The rebase diff: tasks whose committed slot differs from the prediction.
 *
 * By I4 this can only ever contain tasks that were pending a moment ago —
 * committed rows are untouched. That is what bounds a re-sync's blast radius to
 * exactly the lines the user typed while offline (§6.3).
 */
export function relayout(
  previous: Projection,
  next: Projection,
): RelayoutEntry[] {
  const entries: RelayoutEntry[] = [];
  for (const [taskId, nextTask] of Object.entries(next.tasks)) {
    const before = previous.tasks[taskId];
    if (!before) continue;
    if (before.page_id === nextTask.page_id && before.line_index === nextTask.line_index) {
      continue;
    }
    entries.push({
      task_id: taskId,
      old_page_id: before.page_id,
      old_line_index: before.line_index,
      new_page_id: nextTask.page_id,
      new_line_index: nextTask.line_index,
    });
  }
  return entries;
}

/** The tasks on one page, in slot order — what `PageCard` renders. */
export function tasksOnPage(projection: Projection, pageId: string): Task[] {
  return Object.values(projection.tasks)
    .filter((t) => t.page_id === pageId)
    .sort((a, b) => a.line_index - b.line_index);
}

/** The writing page, i.e. the Final Page where new tasks land. */
export function finalPage(projection: Projection): Page | undefined {
  return writingPage(projection);
}

/** Whether a task is settled (done, re-entered, or dismissed). */
export function isSettled(state: TaskState): boolean {
  return state !== 'Active';
}

export type { Notebook };